import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useSetAtom } from 'jotai';
import type { SavedView } from '../../interfaces/SavedView';
import { PowerBiDeleteConfirmDialog } from './power-bi-delete-confirm-dialog';
import { toastAtom } from '../../atoms/toastAtom';
import { ToastVariant } from '../../constants/toast-variant';
import styles from '../power-bi/power-bi-page.module.scss';

interface PowerBiDeleteViewProps {
  activeView: SavedView | null;
  onDeleteView: (viewId: string) => Promise<void>;
  onDeleteSuccess?: () => void;
}

export function PowerBiDeleteView({
  activeView,
  onDeleteView,
  onDeleteSuccess,
}: PowerBiDeleteViewProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const setToast = useSetAtom(toastAtom);

  const handleConfirm = async () => {
    if (!activeView) return;
    setIsDeleting(true);
    try {
      await onDeleteView(activeView.viewId);
      setShowDialog(false);
      setToast({
        message: `"${activeView.viewName}" deleted successfully.`,
        variant: ToastVariant.SUCCESS,
      });
      onDeleteSuccess?.();
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to delete view. Please try again.',
        variant: ToastVariant.ERROR,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <button
        className={`${styles['toolbar-btn']} ${styles['toolbar-icon-btn']} ${styles['toolbar-icon-delete-btn']}`}
        onClick={() => setShowDialog(true)}
        disabled={!activeView}
        title={
          activeView
            ? `Delete "${activeView.viewName}"`
            : 'Select a saved view first'
        }
        aria-label="Delete view"
      >
        <Trash2 size={14} aria-hidden="true" />
      </button>

      {showDialog && activeView && (
        <PowerBiDeleteConfirmDialog
          viewName={activeView.viewName}
          isDeleting={isDeleting}
          onClose={() => setShowDialog(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
